import { useEffect, useState } from "react";
import { X, Printer } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, inr } from "./badges";
import { toast } from "sonner";

export default function InvoiceModal({ orderId, onClose }) {
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/orders/${orderId}/invoice`).then((r) => setInv(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [orderId]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" data-testid="invoice-modal">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-8 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="overline-label">Tax Invoice</p>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">{inv?.invoice_no || "…"}</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} data-testid="invoice-print-btn" className="btn-ghost-b2b !px-3 !py-2 text-xs"><Printer className="h-3.5 w-3.5" /> Print</button>
            <button onClick={onClose} data-testid="invoice-close-btn" className="p-2 text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
          </div>
        </div>
        {!inv ? <p className="py-10 text-center text-sm text-slate-500">Loading…</p> : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Seller</p>
                <p className="font-semibold text-slate-900 mt-1">{inv.seller.name}</p>
                <p className="text-xs text-slate-500">GSTIN: {inv.seller.gstin}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Buyer</p>
                <p className="font-semibold text-slate-900 mt-1">{inv.buyer.company || inv.buyer.name}</p>
                <p className="text-xs text-slate-500">GSTIN: {inv.buyer.gstin}</p>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">Order {inv.order_no} · {fmtDate(inv.date)} · {inv.payment_method.toUpperCase()} · Ship to: {inv.delivery_address}</p>
            <table className="mt-4 w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-2">Item</th><th>Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th>
              </tr></thead>
              <tbody>
                {inv.items.map((it) => (
                  <tr key={it.product_id} className="border-b border-slate-100">
                    <td className="py-2"><p className="font-semibold">{it.name}</p><p className="text-[11px] font-mono2 text-slate-400">{it.sku}</p></td>
                    <td>{it.qty} {it.unit}</td>
                    <td className="text-right">{inr(it.price)}</td>
                    <td className="text-right font-semibold">{inr(it.price * it.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="mt-4 ml-auto max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-semibold">{inr(inv.subtotal)}</dd></div>
              {inv.discount > 0 && <div className="flex justify-between text-emerald-700"><dt>{inv.promo?.label || "Discount"}</dt><dd className="font-semibold">− {inr(inv.discount)}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">Taxable value</dt><dd className="font-semibold">{inr(inv.taxable_value ?? inv.subtotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">CGST 9%</dt><dd className="font-semibold">{inr(inv.cgst)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">SGST 9%</dt><dd className="font-semibold">{inr(inv.sgst)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Delivery</dt><dd className="font-semibold">{inr(inv.delivery_fee)}</dd></div>
              <div className="flex justify-between border-t border-slate-200 pt-2"><dt className="font-bold">Grand total</dt><dd className="font-display font-bold text-lg" data-testid="invoice-total">{inr(inv.total)}</dd></div>
            </dl>
            <p className="mt-6 text-[11px] text-slate-400">Generated by MaterialXpress · Intra-state supply (CGST+SGST) · This is a system-generated invoice.</p>
          </>
        )}
      </div>
    </div>
  );
}
